import { useState } from 'react';
import { NO_VENUE, type SortKey } from './catalog';
import type { PlainKey } from '../i18n/i18n';
import { useT } from '../i18n/useT';

const SORT_LABELS: Record<SortKey, PlainKey> = {
  id: 'catalog.sort.id',
  mid: 'common.yes_price',
  name: 'catalog.sort.name',
  vol: 'volume.sort',
};
const VENUES_COLLAPSED = 12;

export interface CatalogControlsProps {
  query: string;
  onQuery: (q: string) => void;
  sort: SortKey;
  onSort: (s: SortKey) => void;
  venues: [string, number][];
  selected: ReadonlySet<string>;
  onToggleVenue: (v: string) => void;
  onClearVenues: () => void;
}

const chip = 'shrink-0 rounded-full border px-2.5 py-1 text-xs transition-colors';
const chipOn = 'border-accent bg-accent/15 text-accent';
const chipOff = 'border-border text-muted hover:text-fg';

export default function CatalogControls(p: CatalogControlsProps) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? p.venues : p.venues.slice(0, VENUES_COLLAPSED);
  // Never hide a selected venue behind the collapse.
  const hiddenSelected = expanded ? [] : p.venues.slice(VENUES_COLLAPSED).filter(([v]) => p.selected.has(v));
  const list = [...shown, ...hiddenSelected];
  return (
    <div className="space-y-3" data-tour="catalog-controls">
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="search"
          value={p.query}
          onChange={(e) => p.onQuery(e.target.value)}
          placeholder={t('catalog.search_placeholder')}
          aria-label={t('catalog.search_aria')}
          className="w-full rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm placeholder:text-muted sm:flex-1"
        />
        <label className="flex items-center gap-2 text-sm text-muted">
          <span className="shrink-0">{t('catalog.sort')}</span>
          <select
            value={p.sort}
            onChange={(e) => p.onSort(e.target.value as SortKey)}
            aria-label={t('catalog.sort_aria')}
            className="w-full rounded-lg border border-border bg-surface-2 px-2 py-2 text-sm text-fg sm:w-auto"
          >
            {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
              <option key={k} value={k}>
                {t(SORT_LABELS[k])}
              </option>
            ))}
          </select>
        </label>
      </div>
      {p.venues.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t('catalog.venue_filter')}>
          <button
            type="button"
            className={`${chip} ${p.selected.size === 0 ? chipOn : chipOff}`}
            aria-pressed={p.selected.size === 0}
            onClick={p.onClearVenues}
          >
            {t('venue.all')}
          </button>
          {list.map(([v, n]) => {
            const on = p.selected.has(v);
            return (
              <button
                key={v}
                type="button"
                aria-pressed={on}
                onClick={() => p.onToggleVenue(v)}
                className={`${chip} ${on ? chipOn : chipOff}`}
              >
                {v === NO_VENUE ? t('catalog.no_venue') : <span translate="no">{v}</span>} <span className="num opacity-70">{n}</span>
              </button>
            );
          })}
          {p.venues.length > VENUES_COLLAPSED ? (
            <button
              type="button"
              className="px-1 text-xs text-accent hover:underline"
              onClick={() => setExpanded((x) => !x)}
            >
              {expanded ? t('catalog.fewer_venues') : t('catalog.more_venues', { n: p.venues.length - VENUES_COLLAPSED })}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
